/**
 * A conversation on a canvas.
 *
 * Nodes are boxes, answers are wires between them. It pans, it zooms, boxes are
 * dragged where somebody wants them, and a wire is drawn by pulling from an
 * answer onto the line it should lead to. That is what makes a conversation
 * readable: which answer goes where, what rejoins what, and where it ends are
 * all facts about a shape, and a shape has to be looked at.
 *
 * The canvas draws and moves. What a node says and does is edited beside it, so
 * this file stays about the picture.
 */

import { useEffect, useRef, useState } from 'react';
import {
  COLUMN,
  ROW,
  boundsOf,
  placeAll,
  takesInput,
  troubled,
  waysOut,
  type Node,
  type NodeKind,
  type Placed,
} from './graph';

/** How big a node is drawn, which the wires need to know to meet its edges. */
const NODE_WIDTH = 200;

/** How tall the name is, and how tall one way out of a node is under it. */
const HEAD_HEIGHT = 26;
const WAY_HEIGHT = 22;

const ZOOM = { least: 0.35, most: 1.6 };

export function Graph({
  nodes,
  open,
  view,
  onView,
  onCreate,
  onOpen,
  onMove,
  onMoved,
  onConnect,
  onCarry,
  onForget,
}: {
  nodes: readonly Node[];
  /** Which node is being edited, drawn lit. */
  open: string | null;
  onOpen: (id: string) => void;
  /**
   * Somebody is dragging a node, and has let go of it.
   *
   * Two callbacks because they cost different things: the first happens on
   * every frame of a drag and only moves the picture, and the second is the one
   * that writes it down. Sending the conversation over on every pointer move
   * threw a kilobyte a frame at the server and got the socket closed for it.
   */
  onMove: (moves: readonly { id: string; x: number; y: number }[]) => void;
  onMoved: (moves: readonly { id: string; x: number; y: number }[]) => void;
  /**
   * Somebody pulled a wire from one of a node's ways out onto another node.
   *
   * The way out is named by which one it is, since an answer has no name of its
   * own and a branch has exactly two.
   */
  onConnect: (from: string, way: number, to: string) => void;
  /**
   * Somebody grabbed a node by its handle to carry it out of the canvas.
   *
   * A separate gesture from dragging it about, because it means something
   * else entirely: dropping one on the quest window is how a step comes to
   * wait on a particular thing being said.
   */
  onCarry?: (id: string, event: React.DragEvent) => void;
  /**
   * Where the canvas is looking, so whatever puts nodes down can put them here.
   *
   * Held by the window around this rather than in here: a node added from the
   * bar went to the far end of the tree, which is off the screen the moment a
   * conversation is more than a few lines long.
   */
  view: { x: number; y: number; zoom: number };
  onView: (view: { x: number; y: number; zoom: number }) => void;
  /**
   * A wire pulled onto empty canvas, which is a node that does not exist yet.
   *
   * Answering with a kind puts one down where it was dropped, already wired to
   * whatever the wire came from.
   */
  onCreate?: (from: string, way: number, kind: NodeKind, at: { x: number; y: number }) => void;
  /**
   * Delete pressed with nodes picked out on this canvas.
   *
   * Handled here rather than by the window around it because this is what knows
   * which nodes are picked, and because the world editor is listening for the
   * same key: whoever was last touched should be the one it means.
   */
  onForget?: (ids: readonly string[]) => void;
}): JSX.Element {
  const setView = onView;
  const [dragging, setDragging] = useState<{
    kind: 'pan' | 'node' | 'wire' | 'marquee';
    id?: string;
    way?: number;
    /**
     * Every node this drag is moving, and where each one was when it started.
     *
     * A drag on one of several picked nodes moves all of them, which is the
     * whole point of picking several. Where each began is kept because the
     * offset has to be applied to its own position rather than to the one under
     * the pointer.
     */
    held?: readonly { id: string; x: number; y: number }[];
    fromX: number;
    fromY: number;
    atX: number;
    atY: number;
    /** Where the pointer is in the canvas's own numbers, for drawing a wire. */
    overX: number;
    overY: number;
  } | null>(null);

  /**
   * Which nodes are picked out.
   *
   * Drawn round with the marquee, moved together, and left alone by everything
   * else: a conversation of forty lines is rearranged in handfuls, not one node
   * at a time.
   */
  const [picked, setPicked] = useState<readonly string[]>([]);

  const surface = useRef<HTMLDivElement | null>(null);

  /**
   * Whether this canvas is the thing being worked in.
   *
   * The world editor listens for Delete on the window, and so does this: with
   * a conversation open over the world, one key would otherwise mean two
   * things. Whichever was pressed last is the one that gets it.
   */
  const here = useRef(false);

  useEffect(() => {
    const pressed = (event: PointerEvent): void => {
      const root = surface.current?.closest('.conversation');
      here.current =
        root !== null && root !== undefined && event.target instanceof globalThis.Node
          ? root.contains(event.target)
          : false;
    };

    window.addEventListener('pointerdown', pressed, true);
    return () => window.removeEventListener('pointerdown', pressed, true);
  }, []);

  /**
   * Delete takes the picked nodes out, and nothing else.
   *
   * Never while something is being typed into: pressing Delete halfway through
   * a word means to rub out a letter, and a node deleted for it is a node
   * nobody meant to lose. Taken in the capture phase and stopped there, so the
   * world editor underneath does not also act on it.
   */
  useEffect(() => {
    if (!onForget) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (!here.current) return;

      const into = document.activeElement;
      const typing =
        into instanceof HTMLInputElement ||
        into instanceof HTMLTextAreaElement ||
        into instanceof HTMLSelectElement ||
        (into instanceof HTMLElement && into.isContentEditable);
      if (typing) return;

      const going = picked.length > 0 ? picked : open ? [open] : [];
      if (going.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      setPicked([]);
      onForget(going);
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onForget, picked, open]);

  /**
   * Follow the pointer even when it leaves the canvas.
   *
   * Guarded, because capture is refused for a pointer the browser no longer
   * considers active, and a drag that throws here is a drag that never starts.
   */
  const follow = (element: Element | null, pointerId: number): void => {
    try {
      element?.setPointerCapture(pointerId);
    } catch {
      // The gesture works without it; it just stops at the edge of the canvas.
    }
  };
  const placed = placeAll(nodes);

  /** A screen point in the canvas's own numbers. */
  const inCanvas = (screenX: number, screenY: number): { x: number; y: number } => {
    const box = surface.current?.getBoundingClientRect();
    const left = box?.left ?? 0;
    const top = box?.top ?? 0;

    return {
      x: (screenX - left - view.x) / view.zoom,
      y: (screenY - top - view.y) / view.zoom,
    };
  };

  const move = (event: React.PointerEvent): void => {
    if (!dragging) return;

    const at = inCanvas(event.clientX, event.clientY);

    if (dragging.kind === 'pan') {
      setView({
        ...view,
        x: view.x + (event.clientX - dragging.atX),
        y: view.y + (event.clientY - dragging.atY),
      });
      setDragging({ ...dragging, atX: event.clientX, atY: event.clientY });
      return;
    }

    if (dragging.kind === 'node' && dragging.held) {
      // How far the pointer has come, applied to where each node started.
      const byX = at.x - dragging.fromX;
      const byY = at.y - dragging.fromY;

      onMove(
        dragging.held.map((one) => ({
          id: one.id,
          x: Math.round(one.x + byX),
          y: Math.round(one.y + byY),
        })),
      );
      return;
    }

    setDragging({
      ...dragging,
      atX: event.clientX,
      atY: event.clientY,
      overX: at.x,
      overY: at.y,
    });
  };

  /**
   * A wire let go of over empty canvas, waiting to be told what to put there.
   *
   * Held until a kind is chosen or the menu is dismissed, so the wire has
   * somewhere to arrive rather than being thrown away for landing on nothing.
   */
  const [offering, setOffering] = useState<{
    from: string;
    way: number;
    x: number;
    y: number;
  } | null>(null);

  const letGo = (event: React.PointerEvent): void => {
    // Where they ended up, written down once for the lot of them.
    if (dragging?.kind === 'node' && dragging.held) {
      const at = inCanvas(event.clientX, event.clientY);
      const byX = at.x - dragging.fromX;
      const byY = at.y - dragging.fromY;

      onMoved(
        dragging.held.map((one) => ({
          id: one.id,
          x: Math.round(one.x + byX),
          y: Math.round(one.y + byY),
        })),
      );
    }

    // Everything the box was drawn around. Measured off the page rather than
    // worked out from the node kinds: a node's height is whatever its answers
    // made it, and the browser already knows.
    if (dragging?.kind === 'marquee') {
      const box = surface.current?.getBoundingClientRect();
      const left = box?.left ?? 0;
      const top = box?.top ?? 0;

      const corner = {
        x: Math.min(dragging.fromX, dragging.overX) * view.zoom + view.x + left,
        y: Math.min(dragging.fromY, dragging.overY) * view.zoom + view.y + top,
      };
      const far = {
        x: Math.max(dragging.fromX, dragging.overX) * view.zoom + view.x + left,
        y: Math.max(dragging.fromY, dragging.overY) * view.zoom + view.y + top,
      };

      const inside: string[] = [];
      for (const element of surface.current?.querySelectorAll('[data-node]') ?? []) {
        const at = element.getBoundingClientRect();
        if (at.right < corner.x || at.left > far.x) continue;
        if (at.bottom < corner.y || at.top > far.y) continue;

        const id = element.getAttribute('data-node');
        if (id) inside.push(id);
      }

      // Adding to what was already picked when a modifier is down, so a
      // selection can be built out of two sweeps.
      setPicked((held) =>
        event.shiftKey || event.ctrlKey
          ? [...held, ...inside.filter((id) => !held.includes(id))]
          : inside,
      );
    }

    if (dragging?.kind === 'wire' && dragging.id !== undefined && dragging.way !== undefined) {
      // Whatever the pointer came down on. Read off the document rather than
      // from the event, since the pointer is captured by the canvas.
      const under = document.elementFromPoint(event.clientX, event.clientY);
      const landed = under?.closest('[data-node]')?.getAttribute('data-node');

      if (landed && landed !== dragging.id) {
        onConnect(dragging.id, dragging.way, landed);
      } else if (!landed && onCreate) {
        // Dropped on nothing, which is nearly always somebody saying "and then
        // a new one here". Offered rather than assumed: which kind goes there
        // is the whole question.
        const at = inCanvas(event.clientX, event.clientY);
        setOffering({ from: dragging.id, way: dragging.way, x: at.x, y: at.y });
      }
    }

    setDragging(null);
  };

  /**
   * Where a wire leaves a node, and where it arrives.
   *
   * On the pins rather than on the corners: a wire that starts at the dot
   * beside the answer it belongs to says which answer it is without anybody
   * having to work it out.
   */
  const leaves = (from: Placed, way: number): { x: number; y: number } => ({
    x: from.x + NODE_WIDTH,
    y: from.y + HEAD_HEIGHT + WAY_HEIGHT * (way + 0.5),
  });

  const arrives = (to: Placed): { x: number; y: number } => ({
    x: to.x,
    y: to.y + HEAD_HEIGHT / 2,
  });

  const wires: { key: string; d: string; lit: boolean }[] = [];

  for (const node of nodes) {
    const from = placed.get(node.id);
    if (!from) continue;

    const ways = waysOut(node);
    ways.forEach((way, at) => {
      const to = way.goto.length > 0 ? placed.get(way.goto) : undefined;
      if (!to) return;

      const start = leaves(from, at);
      const end = arrives(to);
      const bend = Math.max(40, Math.abs(end.x - start.x) / 2);

      wires.push({
        key: `${node.id}-${at}`,
        d: `M ${start.x} ${start.y} C ${start.x + bend} ${start.y}, ${end.x - bend} ${end.y}, ${end.x} ${end.y}`,
        lit: open === node.id || open === way.goto,
      });
    });
  }

  const dragged =
    dragging?.kind === 'wire' && dragging.id !== undefined
      ? (() => {
          const from = placed.get(dragging.id);
          if (!from) return null;

          const start = leaves(from, dragging.way ?? 0);

          const bend = Math.max(40, Math.abs(dragging.overX - start.x) / 2);
          return `M ${start.x} ${start.y} C ${start.x + bend} ${start.y}, ${dragging.overX - bend} ${dragging.overY}, ${dragging.overX} ${dragging.overY}`;
        })()
      : null;

  return (
    <div
      className="graph"
      ref={surface}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        if ((event.target as HTMLElement).closest('[data-node]')) return;

        follow(event.currentTarget, event.pointerId);
        const at = inCanvas(event.clientX, event.clientY);

        // The left button draws a box round nodes, which is what a left drag on
        // empty space means everywhere else that has a canvas. The middle and
        // right buttons move the canvas about.
        if (event.button !== 0) {
          setDragging({
            kind: 'pan',
            fromX: 0,
            fromY: 0,
            atX: event.clientX,
            atY: event.clientY,
            overX: 0,
            overY: 0,
          });
          return;
        }

        if (!event.shiftKey && !event.ctrlKey) setPicked([]);

        setDragging({
          kind: 'marquee',
          fromX: at.x,
          fromY: at.y,
          atX: event.clientX,
          atY: event.clientY,
          overX: at.x,
          overY: at.y,
        });
      }}
      onPointerMove={move}
      onPointerUp={letGo}
      onPointerCancel={() => setDragging(null)}
      onWheel={(event) => {
        const to = Math.min(
          ZOOM.most,
          Math.max(ZOOM.least, view.zoom * (event.deltaY > 0 ? 0.9 : 1.1)),
        );

        // Held at the pointer, so zooming in looks at what is under it rather
        // than at whatever happened to be in the corner.
        const box = surface.current?.getBoundingClientRect();
        const px = event.clientX - (box?.left ?? 0);
        const py = event.clientY - (box?.top ?? 0);

        setView({
          zoom: to,
          x: px - ((px - view.x) * to) / view.zoom,
          y: py - ((py - view.y) * to) / view.zoom,
        });
      }}
    >
      <div
        className="graph-world"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
      >
        <svg className="graph-wires" overflow="visible">
          {wires.map((wire) => (
            <path key={wire.key} className={wire.lit ? 'wire lit' : 'wire'} d={wire.d} />
          ))}
          {dragged ? <path className="wire dragging" d={dragged} /> : null}
        </svg>

        {/* The box being drawn round things, in the canvas's own numbers so it
            stays where it was started while the view is scaled. */}
        {dragging?.kind === 'marquee' ? (
          <div
            className="graph-marquee"
            style={{
              left: Math.min(dragging.fromX, dragging.overX),
              top: Math.min(dragging.fromY, dragging.overY),
              width: Math.abs(dragging.overX - dragging.fromX),
              height: Math.abs(dragging.overY - dragging.fromY),
            }}
          />
        ) : null}

        {offering ? (
          <div
            className="graph-offer"
            style={{ left: offering.x, top: offering.y }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {(
              [
                ['line', 'Line'],
                ['branch', 'Branch'],
                ['directory', 'Directory'],
                ['checkpoint', 'Checkpoint'],
              ] as const
            ).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                onClick={() => {
                  onCreate?.(offering.from, offering.way, kind, { x: offering.x, y: offering.y });
                  setOffering(null);
                }}
              >
                {label}
              </button>
            ))}

            <button type="button" className="quiet" onClick={() => setOffering(null)}>
              Nothing
            </button>
          </div>
        ) : null}

        {nodes.map((node) => {
          const at = placed.get(node.id);
          if (!at) return null;

          const ways = waysOut(node);
          const branch = node.kind === 'branch';

          return (
            <div
              key={node.id}
              data-node={node.id}
              className={[
                'graph-node',
                node.kind ?? 'line',
                open === node.id ? 'on' : '',
                picked.includes(node.id) ? 'picked' : '',
                troubled(node, nodes) ? 'troubled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ left: at.x, top: at.y, width: NODE_WIDTH }}
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                if ((event.target as HTMLElement).closest('[data-way]')) return;

                event.stopPropagation();
                onOpen(node.id);

                // Held with a modifier, a node joins or leaves what is picked
                // without moving anywhere.
                if (event.shiftKey || event.ctrlKey) {
                  setPicked((held) =>
                    held.includes(node.id)
                      ? held.filter((one) => one !== node.id)
                      : [...held, node.id],
                  );
                  return;
                }

                // Dragging one of several moves all of them. Dragging one that
                // is not picked starts afresh with that one, which is what
                // grabbing something outside a selection always means.
                const moving = picked.includes(node.id) ? picked : [node.id];
                if (!picked.includes(node.id)) setPicked([node.id]);

                const point = inCanvas(event.clientX, event.clientY);
                follow(surface.current, event.pointerId);
                setDragging({
                  kind: 'node',
                  id: node.id,
                  held: moving.flatMap((id) => {
                    const from = placed.get(id);
                    return from ? [{ id, x: from.x, y: from.y }] : [];
                  }),
                  fromX: point.x,
                  fromY: point.y,
                  atX: event.clientX,
                  atY: event.clientY,
                  overX: point.x,
                  overY: point.y,
                });
              }}
            >
              {/* The handle. Grabbing it carries the node out of the canvas
                  rather than moving it about: dropping one on a quest step is
                  how that step comes to wait on this being said. */}
              <div className="graph-node-head">
                {takesInput(node) ? <span className="pin in" /> : null}

                <span
                  className="graph-grip"
                  title="Drag onto a quest step to make it wait for this line"
                  draggable={onCarry !== undefined}
                  onPointerDown={(event) => event.stopPropagation()}
                  onDragStart={(event) => onCarry?.(node.id, event)}
                >
                  ⠿
                </span>

                <span className="graph-node-name">
                  {node.id}
                  {node.once ? (
                    <span className="graph-badge" title="Said once and never again">
                      1st
                    </span>
                  ) : null}
                </span>
              </div>

              {node.kind === 'note' ? (
                <div className="graph-note">{node.text?.slice(0, 200) || 'A note.'}</div>
              ) : (
                <div className="graph-ways">
                  {ways.map((way, index) => (
                    <button
                      key={index}
                      type="button"
                      data-way={index}
                      className={way.goto.length > 0 ? 'graph-way on' : 'graph-way'}
                      title={
                        way.goto.length > 0
                          ? `Goes to ${way.goto}. Pull the pin onto another node to change it.`
                          : 'Goes nowhere. Pull the pin onto a node to send it there.'
                      }
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        const point = inCanvas(event.clientX, event.clientY);
                        follow(surface.current, event.pointerId);
                        setDragging({
                          kind: 'wire',
                          id: node.id,
                          way: index,
                          fromX: 0,
                          fromY: 0,
                          atX: event.clientX,
                          atY: event.clientY,
                          overX: point.x,
                          overY: point.y,
                        });
                      }}
                    >
                      <span className="graph-way-text">
                        {way.label.slice(0, 20) || (branch ? '—' : 'Go on')}
                      </span>
                      <span className="pin out" />
                    </button>
                  ))}

                  {ways.length === 0 ? (
                    <span className="graph-way quiet">
                      <span className="graph-way-text">
                        {node.kind === 'directory' ? 'No choices yet' : 'Ends here'}
                      </span>
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="graph-zoom">
        {picked.length > 0 ? (
          <button type="button" title="Let go of them" onClick={() => setPicked([])}>
            {picked.length} picked
          </button>
        ) : null}

        <button
          type="button"
          title="Bring the whole conversation into view"
          onClick={() => {
            // Everything at once, however wide it has grown. Panning to find a
            // node that is off the edge is fine when a conversation is five
            // lines and hopeless when it is fifty.
            const box = surface.current?.getBoundingClientRect();
            const width = box?.width ?? 800;
            const height = box?.height ?? 600;
            const bounds = boundsOf(placed.values());

            const spread = {
              x: bounds.maxX - bounds.minX + NODE_WIDTH,
              y: bounds.maxY - bounds.minY + ROW,
            };

            // Never past life size: a two-node conversation blown up to fill
            // the canvas is not fitted, it is magnified.
            const zoom = Math.min(
              1,
              Math.max(ZOOM.least, Math.min((width - 40) / spread.x, (height - 40) / spread.y)),
            );

            setView({
              zoom,
              x: (width - spread.x * zoom) / 2 - bounds.minX * zoom,
              y: (height - spread.y * zoom) / 2 - bounds.minY * zoom,
            });
          }}
        >
          Fit
        </button>
        <span>{Math.round(view.zoom * 100)}%</span>
      </div>
    </div>
  );
}

export { COLUMN, ROW };
